import cx from 'classnames';

interface FeedbackQuoteBlockProps {
  /** The selection this rating will carry. */
  quote: string;
  /** Colors the bar and the quote mark after the verdict being recorded. */
  isPositive: boolean;
  /** Drops the quote from the rating. */
  onClear: () => void;
}

/** Tallest the quote may grow before it scrolls inside the block. */
const QUOTE_MAX_HEIGHT = 96;

/**
 * The operator's highlighted selection inside the feedback card. A short
 * header sits above the bordered quote zone so the block reads as their
 * own highlight rather than part of the form; the zone scrolls for long
 * selections; × clears it here as well as beside the thumbs.
 */
export function FeedbackQuoteBlock({
  quote,
  isPositive,
  onClear,
}: FeedbackQuoteBlockProps) {
  const verdictColor = isPositive ? 'var(--green)' : 'var(--red)';

  return (
    <div
      data-testid="chat-feedback-quote-block"
      className="tw-flex tw-flex-col tw-gap-4"
    >
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-8">
        <span
          data-testid="chat-feedback-quote-header"
          className="tw-inline-flex tw-items-center tw-gap-4"
          style={{
            // 10px caption isn't in the type scale.
            fontSize: 10,
            color: 'var(--label-quaternary)',
            lineHeight: 1,
          }}
        >
          <span
            aria-hidden
            style={{ fontSize: 12, lineHeight: 1, color: verdictColor }}
          >
            ❝
          </span>
          Your highlight from this reply
        </span>
        <button
          type="button"
          aria-label="Clear quoted text"
          data-testid="chat-feedback-quote-block-clear-button"
          onClick={onClear}
          className="tw-border-none tw-bg-transparent tw-p-0 tw-cursor-pointer tw-shrink-0"
          style={{
            color: 'var(--label-quaternary)',
            fontSize: 13,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>
      <div
        data-testid="chat-feedback-quote-text"
        className={cx(
          'tw-italic',
          'tw-text-12',
          'tw-overflow-y-auto',
          'tw-whitespace-pre-wrap',
          'tw-break-words',
        )}
        style={{
          color: 'var(--label-secondary)',
          borderLeft: `3px solid ${verdictColor}`,
          paddingLeft: 8,
          paddingRight: 4,
          maxHeight: QUOTE_MAX_HEIGHT,
        }}
      >
        {quote}
      </div>
    </div>
  );
}
